import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import BPRSForm from "./BPRSForm";
import { createBprsAssessment } from "../../../store/slices/bprsAssessmentsSlice";
import { fetchPatient } from "../../../store/slices/patientsSlice"; // Added this import

const AddBPRS = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const [formData, setFormData] = useState({
        caseId,
        patientId,
        clientName: "",
        clientId: "",
        date: new Date().toISOString().split("T")[0],
        md: "",
        clientName: "",
        diagnosis: "",
        assessedBy: "",

        somaticConcern: "",
        anxiety: "",
        emotionalWithdrawal: "",
        conceptualDisorganization: "",
        guiltFeelings: "",
        tension: "",
        mannerismsPosturing: "",
        grandiosity: "",
        depressiveMood: "",
        hostility: "",
        suspiciousness: "",
        hallucinatoryBehavior: "",
        motorRetardation: "",
        uncooperativeness: "",
        unusualThoughtContent: "",
        bluntedAffect: "",
        excitement: "",
        disorientation: ""
    });

    useEffect(() => {
        if (!patientId) {
            Swal.fire("Error", "Patient ID is required.", "error");
            navigate("/patients");
            return;
        }

        const loadPatient = async () => {
            try {
                // 1. FETCH PATIENT FROM BACKEND
                const data = await dispatch(fetchPatient(patientId)).unwrap();

                setFormData((prev) => ({
                    ...prev,
                    clientName: data.first_name + " " + data.last_name,
                    clientId: data.unique_id,
                    diagnosis: data.diagnosis || "",
                    md: data.attending_doctor || ""
                }));
            } catch (error) {
                Swal.fire("Error", "Failed to fetch patient data.", "error");
                navigate("/patients");
            }
        };

        loadPatient();
    }, [dispatch, patientId, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        // convert: "2 = Very mild" → "Very mild"
        const pureLabel = e.target.getAttribute("data-label")
            ?.split("=")[1]
            ?.trim() || "";

        setFormData((prev) => ({
            ...prev,
            [name]: value,               // score
            [`${name}Label`]: pureLabel // only descriptive text
        }));
    };

    const calculateTotal = () => {
        const questionKeys = [
            "somaticConcern",
            "anxiety",
            "emotionalWithdrawal",
            "conceptualDisorganization",
            "guiltFeelings",
            "tension",
            "mannerismsPosturing",
            "grandiosity",
            "depressiveMood",
            "hostility",
            "suspiciousness",
            "hallucinatoryBehavior",
            "motorRetardation",
            "uncooperativeness",
            "unusualThoughtContent",
            "bluntedAffect",
            "excitement",
            "disorientation"
        ];

        return questionKeys.reduce(
            (sum, key) => sum + (parseInt(formData[key] || 0, 10)),
            0
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const total = calculateTotal();

        // if (total < 18) {
        //     Swal.fire("Incomplete", "Please complete all 18 items.", "warning");
        //     return;
        // }

        // Build questionPayload with snake_case fields
        const mapField = (camel, snake) => ({
            [`${snake}_score`]: parseInt(formData[camel] || 0, 10),
            [`${snake}_value`]: formData[`${camel}Label`] || ""
        });

        const questionPayload = {
            ...mapField("somaticConcern", "somatic_concern"),
            ...mapField("anxiety", "anxiety"),
            ...mapField("emotionalWithdrawal", "emotional_withdrawal"),
            ...mapField("conceptualDisorganization", "conceptual_disorganization"),
            ...mapField("guiltFeelings", "guilt_feelings"),
            ...mapField("tension", "tension"),
            ...mapField("mannerismsPosturing", "mannerisms_posturing"),
            ...mapField("grandiosity", "grandiosity"),
            ...mapField("depressiveMood", "depressive_mood"),
            ...mapField("hostility", "hostility"),
            ...mapField("suspiciousness", "suspiciousness"),
            ...mapField("hallucinatoryBehavior", "hallucinatory_behavior"),
            ...mapField("motorRetardation", "motor_retardation"),
            ...mapField("uncooperativeness", "uncooperativeness"),
            ...mapField("unusualThoughtContent", "unusual_thought_content"),
            ...mapField("bluntedAffect", "blunted_affect"),
            ...mapField("excitement", "excitement"),
            ...mapField("disorientation", "disorientation"),
        };

        // Final payload for backend
        const payload = {
            patient_id: formData.patientId || null,
            case_id: formData.caseId || null,
            client_name: formData.clientName || "",
            client_id: formData.clientId || "",
            md: formData.md || "",
            diagnosis: formData.diagnosis || "",
            assessed_by: formData.assessedBy || "",
            ...questionPayload
        };

        try {
            const res = await dispatch(createBprsAssessment(payload)).unwrap();

            Swal.fire({
                title: "Saved!",
                text: `BPRS Assessment submitted. Total Score: ${total}`,
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });

            navigate(`/phase04/bprs/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
        } catch (error) {
            Swal.fire("Error", "Failed to save BPRS assessment.", "error");
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <BPRSForm formData={formData} handleChange={handleChange} />

                            <div className="col-12 d-flex justify-content-end mt-3">
                                <button type="submit" className="btn btn-primary px-4 sticky-form-submit">
                                    <i className="ri-save-2-line me-1"></i>
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddBPRS;
